import { useApi } from '~/api'
import { activeQueryLicenceScope, reportCapabilitiesQueryKey } from '~/api/models/report-service'
import {
  hasReportCapability,
  REPORT_CAPABILITIES_FALLBACK,
  resolveReportCapabilities,
  type ReportCapabilityKey
} from '~/composables/useReportCapabilities'
import { queryClient } from '~/plugins/vue-query.client'
import { useAuthStore } from '~/stores/authStore'
import type { ServiceCapabilities } from '~/types/report-service'

const DENIED_ROUTE = '/reports'
const CAPABILITIES_STALE_TIME = 1000 * 60 * 5

const loadCapabilities = async (): Promise<ServiceCapabilities> => {
  const api = useApi()

  try {
    const answer = await queryClient.ensureQueryData({
      queryKey: reportCapabilitiesQueryKey(activeQueryLicenceScope()),
      queryFn: () => api.reportService.getCapabilities(),
      staleTime: CAPABILITIES_STALE_TIME,
      retry: 0
    })
    return resolveReportCapabilities(answer)
  } catch (error) {
    console.warn('[Capability Middleware] Failed to load report capabilities:', error)
    return REPORT_CAPABILITIES_FALLBACK
  }
}

export default defineNuxtRouteMiddleware(async (to) => {
  const requirement = to.meta.requiredCapabilities as ReportCapabilityKey | ReportCapabilityKey[] | undefined

  if (!requirement) {
    return
  }

  const authStore = useAuthStore()

  if (!authStore.isAuthenticated) {
    return navigateTo({
      path: '/login',
      query: { redirect: to.fullPath }
    })
  }

  if (!authStore.activeLicence) {
    return
  }

  const required = Array.isArray(requirement) ? requirement : [requirement]
  const capabilities = await loadCapabilities()

  if (required.every((key) => hasReportCapability(capabilities, key))) {
    return
  }

  console.warn('[Capability Middleware] Access denied:', { required, path: to.path })

  return navigateTo((to.meta.capabilityRedirect as string | undefined) ?? DENIED_ROUTE)
})
